import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mail, Phone, MapPin, Building, FileText, Award,
  Heart, ArrowUp
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getPath } from '../config/routes';

const Footer: React.FC = () => {
  const { language, t } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navItems = [
    { key: 'services', labelKey: 'nav.services' },
    { key: 'pricing', labelKey: 'nav.pricing' },
    { key: 'faq', labelKey: 'nav.faq' },
    { key: 'shipping', labelKey: 'nav.shipping' },
    { key: 'contact', labelKey: 'nav.contact' },
    { key: 'privacy', labelKey: 'footer.privacy' },
    { key: 'terms', labelKey: 'footer.terms' },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-gray-900 to-black text-white">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />

      <div className="relative pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">

            <div>
              <Link to={getPath('home', language)} className="flex items-center space-x-3 mb-6 group">
                <div className="relative w-12 h-12 transform transition-transform group-hover:scale-110">
                  <img src="/logo.png" alt="Pirkanmaan Potkurikorjaus" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Pirkanmaan Potkurikorjaus</h3>
                  <p className="text-sm text-gray-400">{t('footer.tagline')}</p>
                </div>
              </Link>

              <p className="text-gray-300 mb-6 leading-relaxed">
                {t('footer.description')}
              </p>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Building className="w-4 h-4" />
                  <span>FennoFuture</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <FileText className="w-4 h-4" />
                  <span>{t('footer.businessId')}: 3203489-7</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Award className="w-4 h-4" />
                  <span>{t('footer.founded')}: 2021</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6 flex items-center gap-2">
                <div className="w-1 h-6 bg-gradient-to-b from-cyan-500 to-blue-500 rounded-full" />
                {t('footer.quickLinks')}
              </h4>
              <ul className="space-y-3">
                {navItems.map((item, index) => (
                  <li key={index}>
                    <Link
                      to={getPath(item.key, language)}
                      className="text-gray-300 hover:text-white transition-colors duration-300 flex items-center gap-2 group"
                    >
                      <span className="w-0 group-hover:w-4 h-px bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-300" />
                      {t(item.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6 flex items-center gap-2">
                <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full" />
                {t('footer.contact')}
              </h4>
              <ul className="space-y-4">
                <li>
                  <a href="mailto:info@potkurikorjaus.fi" className="flex items-start gap-3 text-gray-300 hover:text-white transition-colors group">
                    <Mail className="w-5 h-5 mt-0.5 group-hover:scale-110 transition-transform" />
                    <div>
                      <div className="font-medium">info@potkurikorjaus.fi</div>
                      <div className="text-xs text-gray-500">{t('contact.email')}</div>
                    </div>
                  </a>
                </li>
                <li>
                  <a href="tel:+358400416466" className="flex items-start gap-3 text-gray-300 hover:text-white transition-colors group">
                    <Phone className="w-5 h-5 mt-0.5 group-hover:scale-110 transition-transform" />
                    <div>
                      <div className="font-medium">+358 400 416 466</div>
                      <div className="text-xs text-gray-500">{t('contact.phone')}</div>
                    </div>
                  </a>
                </li>
                <li>
                  <div className="flex items-start gap-3 text-gray-300">
                    <MapPin className="w-5 h-5 mt-0.5" />
                    <div>
                      <div className="font-medium">Turuntie 3, 37830 Akaa</div>
                      <div className="text-xs text-gray-500">Akaa / Sastamala / Urjala</div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <span>© 2026 Pirkanmaan Potkurikorjaus.</span>
                <span>{t('footer.copyright')}</span>
              </div>

              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <span>{t('footer.madeWith')}</span>
                <Heart className="w-4 h-4 text-red-500" />
                <span>{t('footer.inFinland')}</span>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5 transition-transform group-hover:-translate-y-1" />
        </button>
      </div>
    </footer>
  );
};

export default Footer;
